import { Cancel } from '@mui/icons-material';
import {
  Alert,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  Grid2,
  TextField,
  Typography
} from '@mui/material';
import { Department } from '@thro-types/billing';

import { Field, Form, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useDepartmentMutation } from 'api/hooks/billing/department';
import { SaveButton } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import {
  departmentSettingsValidationSchema,
  useDepartmentSettingsInitialValue
} from './validation';

interface DepartmentProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  department: Department | undefined;
}

export const DepartmentDrawer = (props: DepartmentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { creation, update } = useDepartmentMutation();

  const handleSaveClick = async values => {
    try {
      if (creation.isPending || update.isPending) {
        return;
      }

      delete values.id;

      if (props.department) {
        await update.mutateAsync({
          key: props.department.departmentId,
          data: { ...values }
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.billingSettings.mutation.departmentUpdateSuccess())
          )
        );
        props.setOpen(false);
      } else {
        await creation.mutateAsync({
          ...values
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.billingSettings.mutation.departmentCreationSuccess())
          )
        );
      }
      props.setOpen(false);
    } catch (error) {
      if (props.department) {
        dispatch(
          AlertActions.error(
            t(translations.pages.billingSettings.mutation.departmentUpdateFailed())
          )
        );
      } else {
        dispatch(
          AlertActions.error(
            t(translations.pages.billingSettings.mutation.departmentCreationFailed())
          )
        );
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={props.open}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
      onClose={() => props.setOpen(false)}
    >
      <Formik
        initialValues={useDepartmentSettingsInitialValue(props.department)}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={departmentSettingsValidationSchema(t)}
        validateOnMount
      >
        {({ touched, errors, isValid }) => (
          <Form>
            <Grid2
              container
              padding={2}
              flexDirection={'column'}
              height={'100vh'}
              justifyContent={'flex-end'}
              gap={2}
            >
              <Grid2 container size={12} gap={1} flexDirection={'column'}>
                <Typography variant="h5" fontWeight={'bold'}>
                  {props.department?.id
                    ? t(translations.pages.billingSettings.department.drawer.update())
                    : t(translations.pages.billingSettings.department.drawer.create())}
                </Typography>
                <Divider />
                <Typography variant="body2">
                  {t(translations.pages.billingSettings.department.drawer.info())}
                </Typography>
                <Divider />
              </Grid2>
              <Grid2 container direction={'column'} gap={2}>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.name())}</FormLabel>
                  <Field
                    as={TextField}
                    id="name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.email())}</FormLabel>
                  <Field
                    as={TextField}
                    id="email"
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.common.phone())}</FormLabel>
                  <Field
                    as={TextField}
                    id="phone"
                    name="phone"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    size="small"
                  />
                </FormControl>
              </Grid2>
              <Alert severity="info">
                {t(translations.pages.billingSettings.department.drawer.editHint())}
              </Alert>
              <Grid2 size={12} alignContent={'center'} marginTop={'auto'}>
                <Divider sx={{ marginLeft: 1, marginRight: 0.6, flex: 1 }} />
              </Grid2>
              <Grid2 container gap={2} justifyContent={'flex-end'} alignItems={'flex-end'}>
                <Button
                  variant="contained"
                  startIcon={<Cancel />}
                  color="error"
                  onClick={() => props.setOpen(false)}
                  disabled={creation.isPending || update.isPending}
                >
                  {t(translations.common.cancel())}
                </Button>
                <SaveButton
                  isSubmitting={creation.isPending || update.isPending}
                  isValid={isValid}
                />
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};
